import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { SETTINGS_GROUPS } from '@ekhodealer/ekho-common';
import { type BreadcrumbItem, Stack } from '@ekhodealer/ekho-common/rev';

import { PageLayout } from '@common/components/PageLayout';
import { ProductUpgradeOverlay } from '@common/components/ProductUpgradeOverlay';
import { ProgramSelector } from '@common/components/ProgramSelector';
import { TablePageLayout } from '@common/components/TablePageLayout';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useProductGating } from '@common/hooks/useProductGating';
import { BankAccountForm } from '@features/account-management/components/bank-account/BankAccountForm';
import { BusinessDetailsForms } from '@features/account-management/components/business-details-forms/BusinessDetailsForms';
import { ConfigMgmt } from '@features/account-management/components/config-mgmt/ConfigMgmt';
import { useCombinedServiceSections } from '@features/account-management/hooks/useServiceProgramInfo';
import { getSectionGroup } from '@features/account-management/lib/sectionGroupUtils';
import { AiBdcSettingsPage } from '@features/ai-bdc/components/AiBdcSettingsPage';
import { IntegrationsSettingsPage } from '@features/integrations/components/IntegrationsSettingsPage';
import { InventorySettingsPage } from '@features/inventory/components/InventorySettingsPage';
import { TeamTable } from '@features/team/components/TeamTable';
import { TeamToolbar } from '@features/team/components/TeamToolbar';
import { WebsiteSettingsPage } from '@features/website/components/WebsiteSettingsPage';
import { paths } from '@config/paths';
import { SettingsSection } from '@config/paths/settingsSections';

import './SettingsSubPage.scss';

const sectionTitleMap: Record<SettingsSection, string> = {
	[SettingsSection.AccountBusinessDetails]: 'Business details',
	[SettingsSection.AccountBankAccount]: 'Bank account',
	[SettingsSection.AccountTeam]: 'Team',
	[SettingsSection.ProductsSales]: 'Sales',
	[SettingsSection.ProductsWebsite]: 'Website',
	[SettingsSection.ProductsAiBdc]: 'AI BDC',
	[SettingsSection.SharedIntegrations]: 'Integrations',
	[SettingsSection.SharedInventory]: 'Inventory',
};

export default function SettingsSubPage(): React.ReactNode {
	const { '*': sectionPath } = useParams();
	const spId = useCurrentServicesProviderId();
	const navigate = useNavigate();
	const { getSettingsSectionGate } = useProductGating();
	const combinedSections = useCombinedServiceSections();
	const section = sectionPath as SettingsSection;

	// Sales sub-tabs (checkout, delivery, assembly, etc.) are gated per-service, not per-program-owner.
	// Only show the upgrade overlay if there are zero non-inventory service sections available.
	const hasSalesSections = useMemo(
		() =>
			combinedSections.some((s) => {
				const group = getSectionGroup(s);
				return group !== null && group !== SETTINGS_GROUPS.INVENTORY;
			}),
		[combinedSections],
	);

	const sectionTitle = sectionTitleMap[section];

	// Invalid section — redirect to settings landing
	if (!sectionTitle) {
		return <Navigate to={paths.settings.getHref(spId)} replace />;
	}

	// Team uses TablePageLayout (same as Orders, Leads, etc.)
	if (section === SettingsSection.AccountTeam) {
		return (
			<TablePageLayout
				breadcrumb={{
					items: [
						{
							id: 'settings',
							label: 'Settings',
							onActivate: () => navigate(paths.settings.getHref(spId)),
						},
						{ id: 'team', label: 'Team' },
					],
					currentId: 'team',
				}}
				aboveTableContent={
					<Stack itemsSpacing='16'>
						<TeamToolbar />
					</Stack>
				}
				tableContent={<TeamTable />}
			/>
		);
	}

	const { locked: isLocked, moduleId: gatedModuleId } = getSettingsSectionGate(section);

	let content;
	switch (section) {
		case SettingsSection.AccountBusinessDetails:
			content = <BusinessDetailsForms />;
			break;
		case SettingsSection.AccountBankAccount:
			content = <BankAccountForm />;
			break;
		case SettingsSection.ProductsSales:
			content = <ConfigMgmt />;
			break;
		case SettingsSection.ProductsWebsite:
			content = <WebsiteSettingsPage />;
			break;
		case SettingsSection.ProductsAiBdc:
			content = <AiBdcSettingsPage />;
			break;
		case SettingsSection.SharedIntegrations:
			content = <IntegrationsSettingsPage />;
			break;
		case SettingsSection.SharedInventory:
			content = <InventorySettingsPage />;
			break;
		default:
			return <Navigate to={paths.settings.getHref(spId)} replace />;
	}

	const breadcrumbItems: BreadcrumbItem[] = [
		{
			id: 'settings',
			label: 'Settings',
			onActivate: () => navigate(paths.settings.getHref(spId)),
		},
		{
			id: section,
			label: sectionTitle,
		},
	];

	// This dropdown shows the service access template (program) for the current SP —
	// distinct from the top-level SP selector which switches between services providers.
	// Currently read-only (isDisabled=true in ProgramSelector). When enabled, it would
	// allow switching between program templates to view/edit program-level settings.
	const showProgramSelector =
		section === SettingsSection.ProductsSales || section === SettingsSection.SharedInventory;

	return (
		<PageLayout
			breadcrumb={{ items: breadcrumbItems, currentId: section }}
			trailingContent={showProgramSelector ? <ProgramSelector /> : undefined}
			className='settings-sub-page'>
			<div className='settings-sub-page__content'>{content}</div>
			{isLocked && gatedModuleId && <ProductUpgradeOverlay moduleId={gatedModuleId} />}
			{!isLocked &&
				section === SettingsSection.ProductsSales &&
				!hasSalesSections && <ProductUpgradeOverlay moduleId='transactionLayer' />}
		</PageLayout>
	);
}
